/**
 * AutoModeButton — the composer's auto mode chip and its popover.
 *
 * The chip shows whether this conversation is in auto mode (the agent stops asking
 * before ordinary work — features/agent/core/auto-mode.ts is the rule) and opens a
 * popover with the switch and the conversation's "Always" answers, each of which
 * can be taken back. The server decides what is asked; this only shows and sets it.
 * It also holds the member's own default for NEW chats in this org (a checkbox): it never changes an existing chat.
 *
 * Portals to <body> like DisplayOptionsMenu, so it re-applies the theme class; it
 * opens UPWARD — the composer sits at the bottom of the panel.
 */
import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Zap, X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

export interface AlwaysGrant {
  permission: string
  pattern: string
}

interface Props {
  /** null = not known yet (a chat whose setting hasn't been read) — shown as unknown, never as off. */
  on: boolean | null
  /** The conversation's stored "Always" answers; empty for a chat not created yet, null while not known. */
  grants: readonly AlwaysGrant[] | null
  /** Resolves when saved; the switch is disabled meanwhile, so a double click can't send the same value twice. */
  onToggle: (next: boolean) => void | Promise<void>
  onRevoke: (grant: AlwaysGrant) => void
  /** Called when the popover opens, so the list shows grants given since the last load. */
  onOpen?: () => void
  /** The switch can't be used (a chat the viewer can't send to, or one not loaded yet). Taking a grant back still can. */
  switchDisabled?: boolean
  /** The member's own default for NEW chats in this org — null while not read; omitted = no checkbox. */
  defaultOn?: boolean | null
  /** Resolves when saved; the checkbox is disabled meanwhile. */
  onToggleDefault?: (next: boolean) => void | Promise<void>
  /** Called when the popover opens, to read the default again. */
  onRefreshDefault?: () => void
}

/** The permissions the card can ask about, by the name shown to the person (unknown ones show their raw name). */
const GRANT_LABEL_KEYS: Record<string, string> = {
  write_file: 'autoMode.grant.write_file',
  edit_file: 'autoMode.grant.edit_file',
  office_tool: 'autoMode.grant.office_tool',
  libre_office_tool: 'autoMode.grant.libre_office_tool',
  move_file: 'autoMode.grant.move_file',
  web_save: 'autoMode.grant.web_save',
  move_to_drive: 'autoMode.grant.move_to_drive',
  webfetch: 'autoMode.grant.webfetch',
  task: 'autoMode.grant.task',
  run_batch_llm: 'autoMode.grant.run_batch_llm',
}

export function AutoModeButton({ on, grants, onToggle, onRevoke, onOpen, switchDisabled, defaultOn, onToggleDefault, onRefreshDefault }: Props) {
  const { t } = useTranslation('agent')
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const toggle = async () => {
    if (saving) return
    setSaving(true)
    try { await onToggle(!on) } finally { setSaving(false) }
  }
  const [savingDefault, setSavingDefault] = useState(false)
  const toggleDefault = async () => {
    if (savingDefault || !onToggleDefault || defaultOn == null) return
    setSavingDefault(true)
    try { await onToggleDefault(!defaultOn) } finally { setSavingDefault(false) }
  }
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [pos, setPos] = useState<{ bottom: number; left: number } | null>(null)

  const openPanel = () => {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setPos({ bottom: window.innerHeight - r.top + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - 288 - 8)) })
    setOpen(true)
    onOpen?.()
    onRefreshDefault?.()
  }

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (btnRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const grantLabel = (g: AlwaysGrant) => (GRANT_LABEL_KEYS[g.permission] ? t(GRANT_LABEL_KEYS[g.permission]) : g.permission)

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openPanel())}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-pressed={on ?? undefined}
        data-state={on === null ? 'unknown' : on ? 'on' : 'off'}
        data-testid="auto-mode-chip"
        title={on === null ? t('autoMode.chipUnknownTitle') : on ? t('autoMode.chipOnTitle') : t('autoMode.chipOffTitle')}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] shrink-0 transition-colors ${
          on ? 'bg-brand-600/20 text-brand-400 hover:bg-brand-600/30' : 'themed-text-muted hover:themed-text hover:themed-bg-tertiary'
        }`}
      >
        <Zap size={12} className={on ? 'fill-current' : ''} />
        <span>{t('autoMode.label')}{on === null ? ' ?' : ''}</span>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t('autoMode.title')}
          data-testid="auto-mode-panel"
          className={`${themeClass} fixed w-72 rounded-md border themed-border themed-bg-secondary shadow-lg z-[100]`}
          style={{ bottom: pos.bottom, left: pos.left }}
        >
          <button
            type="button"
            role="switch"
            aria-checked={on === null ? 'mixed' : on}
            aria-describedby="auto-mode-describe"
            onClick={() => { void toggle() }}
            disabled={switchDisabled || saving}
            data-testid="auto-mode-switch"
            className="w-full flex items-center justify-between gap-3 px-3 pt-2.5 pb-1 text-xs font-medium themed-text disabled:opacity-50"
          >
            <span className="flex items-center gap-1.5"><Zap size={12} className="text-brand-400" />{t('autoMode.title')}</span>
            <div className={`w-7 h-4 rounded-full transition-colors flex items-center shrink-0 ${on === null ? 'bg-white/10 justify-center' : on ? 'bg-brand-600 justify-end' : 'bg-white/10 justify-start'}`}>
              <div className="w-3 h-3 rounded-full bg-white mx-0.5 shadow-sm" />
            </div>
          </button>
          <p id="auto-mode-describe" className="px-3 pb-1 text-[11px] leading-snug themed-text-muted">
            {on === null ? t('autoMode.describeUnknown') : on ? t('autoMode.describeOn') : t('autoMode.describeOff')}
          </p>
          <p className="px-3 pb-2.5 text-[10px] leading-snug themed-text-muted">{t('autoMode.appliesNote')}</p>
          {onToggleDefault && (
            <div className="border-t themed-border px-3 py-2">
              <label className={`flex items-start gap-2 text-[11px] themed-text ${defaultOn == null || savingDefault ? 'opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  className="mt-0.5 shrink-0"
                  checked={defaultOn === true}
                  // unknown is drawn as unknown (a dash), never as an unticked "off"
                  ref={(el) => { if (el) el.indeterminate = defaultOn == null }}
                  aria-checked={defaultOn == null ? 'mixed' : defaultOn}
                  disabled={defaultOn == null || savingDefault}
                  onChange={() => { void toggleDefault() }}
                  aria-describedby="auto-mode-default-note"
                  data-testid="auto-mode-default"
                />
                <span>{t('autoMode.defaultLabel')}</span>
              </label>
              <p id="auto-mode-default-note" className="mt-1 pl-5 text-[10px] leading-snug themed-text-muted">
                {defaultOn == null ? t('autoMode.defaultUnknown') : t('autoMode.defaultNote')}
              </p>
            </div>
          )}

          <div className="border-t themed-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide themed-text-muted mb-1">{t('autoMode.alwaysTitle')}</div>
            {grants === null ? (
              <p className="text-[11px] themed-text-muted" data-testid="auto-mode-grants-unknown">{t('autoMode.alwaysUnknown')}</p>
            ) : grants.length === 0 ? (
              <p className="text-[11px] themed-text-muted" data-testid="auto-mode-no-grants">{t('autoMode.alwaysNone')}</p>
            ) : (
              <>
                <ul className="space-y-0.5" data-testid="auto-mode-grants">
                  {grants.map((g) => (
                    <li key={`${g.permission}\u0000${g.pattern}`} className="flex items-center gap-2 text-[11px] themed-text">
                      <span className="flex-1 min-w-0 truncate" title={grantLabel(g)}>{grantLabel(g)}</span>
                      <button
                        type="button"
                        onClick={() => onRevoke(g)}
                        aria-label={t('autoMode.revoke', { what: grantLabel(g) })}
                        title={t('autoMode.revoke', { what: grantLabel(g) })}
                        className="p-0.5 rounded themed-text-muted hover:text-red-400 hover:themed-bg-tertiary shrink-0"
                      >
                        <X size={11} />
                      </button>
                    </li>
                  ))}
                </ul>
                <p className="mt-1.5 text-[10px] themed-text-muted">{t('autoMode.revokeNote')}</p>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
